import type { PublicationInboxItem } from '@/hooks/usePublicationInbox';

import {
  decodePublicationInboxText,
  formatPublicationInboxDate,
  publicationInboxAttentionClasses,
  publicationInboxReferenceDate,
  publicationInboxStateLabel,
} from '@/lib/publicationInboxPresentation';

type Props = {
  items: PublicationInboxItem[];
  selectedId: string | null;
  isLoading: boolean;
  onSelect: (id: string) => void;
};

export default function PublicationInboxList({
  items,
  selectedId,
  isLoading,
  onSelect,
}: Props) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((item) => (
          <div
            key={item}
            className="h-28 animate-pulse rounded-2xl border border-background-200 bg-background-100"
          />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-background-200 bg-background-50 p-8 text-center">
        <i className="ri-inbox-line text-2xl text-foreground-300"></i>
        <p className="mt-2 text-sm font-medium text-foreground-700">
          Nenhuma publicação nesta caixa.
        </p>
        <p className="mt-1 text-xs text-foreground-500">
          Altere a caixa ou refine os filtros.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const tone =
          publicationInboxAttentionClasses(
            item.workflow.attention
          );
        const selected =
          selectedId === item.id;

        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            className={`w-full rounded-2xl border bg-background-50 p-3 text-left transition ${
              selected
                ? 'border-primary-300 ring-2 ring-primary-100'
                : `${tone.border} hover:border-primary-200`
            }`}
          >
            <div className="flex items-start gap-3">
              <span
                className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${tone.dot}`}
              />

              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground-900">
                      {decodePublicationInboxText(item.publication.title)}
                    </p>
                    <p className="mt-0.5 truncate text-xs text-foreground-500">
                      {item.context.clientName ??
                        'Cliente não identificado'}
                      {item.publication.tribunal
                        ? ` • ${item.publication.tribunal}`
                        : ''}
                    </p>
                  </div>

                  <span className="shrink-0 text-[10px] text-foreground-400">
                    {formatPublicationInboxDate(
                      publicationInboxReferenceDate(
                        item.publication
                      )
                    )}
                  </span>
                </div>

                {item.publication.processNumber && (
                  <p className="mt-2 truncate font-mono text-[11px] text-foreground-500">
                    {item.publication.processNumber}
                  </p>
                )}

                <p className="mt-2 line-clamp-2 text-xs leading-5 text-foreground-600">
                  {item.ai.summary ??
                    (decodePublicationInboxText(
                      item.publication.excerpt
                    ) ||
                      publicationInboxStateLabel(
                        item.workflow.state
                      ))}
                </p>

                <div className="mt-2 flex flex-wrap gap-1">
                  {item.badges
                    .slice(0, 4)
                    .map((badge) => (
                      <span
                        key={badge}
                        className="rounded-full border border-background-200 bg-white px-2 py-0.5 text-[10px] font-medium text-foreground-600"
                      >
                        {badge}
                      </span>
                    ))}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}